/**
 * `IDeploymentConfigModel.ts` — the transport for documents that belong to the
 * DEPLOYMENT rather than to a customer.
 *
 * ## Why this exists at all
 *
 * Two documents used to ride `IStorageModel`: the published agent prompts and
 * the AI settings (skill/role gates, per-role model, per-role skill scope).
 * That interface addresses **customer cloud data** — every one of its S3 calls
 * resolves to `account/{customerId}/projectdata/…` — and those two documents
 * were there for exactly one reason: a browser holds raw Cognito credentials,
 * and Cognito credentials can write nowhere else. "Global" was synonymous with
 * "per customer" because nothing else was reachable.
 *
 * Neither document is customer data. They configure the product: two accounts
 * signed into the same deployment should not be running two different agents,
 * and a prompt published by one team should not be invisible to the next. So
 * they moved to the machine that serves the app, and they get their OWN
 * interface rather than a fifth pair of methods on the storage model — the
 * layering is the point. Nothing here takes a `customerId`, and nothing here
 * can be made to write into a customer's bucket by passing the wrong argument.
 *
 * ## Scoped per DEPLOYMENT
 *
 * One document set per deployment — `default`, `jay-testing`, … — for the same
 * reason the prompts document was already keyed that way inside the old
 * customer prefix: a named channel exists to run a DIFFERENT VERSION of the app
 * against the same data, so an origin-wide document would shadow a channel's own
 * bundled prompts and make a channel useless for the thing prompts are most
 * often channelled to test. The cost is deliberate and unchanged: publishing on
 * one channel does not reach the others.
 *
 * ## Reads never need a session; writes always do
 *
 * A GET is unauthenticated — the content is prompt text the client is about to
 * be handed anyway, seeded from public static files in the bundle. A PUT carries
 * the signed-in user's Cognito ID token, which the host verifies against the
 * app's user pool before writing, because the body of a PUT is Spark's system
 * prompt for everyone on the deployment.
 *
 * That asymmetry has a consequence worth stating: **settings and prompts load
 * before sign-in.** They are properties of the deployment, so the login screen
 * and a signed-out tab read exactly what a signed-in one does.
 */

/** Which deployment-scoped document. The service accepts these two names only. */
export type DeploymentConfigDocument = "prompts" | "settings";

export interface IDeploymentConfigModel {
  /**
   * The document's text, or **null when it has never been written** — which is
   * not an error: every key in both documents falls back to a shipped default,
   * so an unwritten deployment runs stock.
   *
   * THROWS on a transient failure. The distinction is load-bearing in both
   * callers: `AppSettingsModel` must not cache a failed read as "loaded" (it
   * would latch stock behaviour until a reload) and `AIPromptStoreModel` must
   * not let a create path overwrite a document it merely failed to read.
   */
  getAsync(document: DeploymentConfigDocument, signal?: AbortSignal): Promise<string | null>;

  /**
   * Publishes the document for the whole deployment. Requires a signed-in user
   * whose token the host accepts; rejects otherwise.
   *
   * No optimistic concurrency — last write wins, exactly as it did on S3. Both
   * callers already re-read and merge immediately before writing, which is where
   * that problem is solved; a second mechanism here would be a second answer.
   */
  putAsync(document: DeploymentConfigDocument, text: string, signal?: AbortSignal): Promise<void>;
}

/** IOC token. Resolve with `Ioc.resolve<IDeploymentConfigModel>(DEPLOYMENT_CONFIG_MODEL)`. */
export const DEPLOYMENT_CONFIG_MODEL = Symbol.for("Campfire.DeploymentConfigModel");
